import { useEffect } from 'react';
import * as yup from 'yup';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import {
	DatovkaNastaveniArchivaceValue,
	useArchivingSettingsQuery,
	useNotificationSettingsQuery,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { usePersonCommunicationData } from '@gov-nx/core/hooks';
import { useDataBoxStoreLists } from '@gov-nx/store';
import { notificationSettingsValues } from '../upozorneni/utils';

export interface DataboxAdditionalSettingsFormData {
	shouldArchiveAutomatically?: boolean;
	shouldArchiveLargeMessagesAutomatically?: boolean;
	emailEnabled?: boolean;
}

interface DataboxAdditionalSettingsOptions {
	dataBoxId?: string;
	onSuccess: () => void;
}

export const useDataboxAdditionalSettings = ({
	dataBoxId,
	onSuccess,
}: DataboxAdditionalSettingsOptions) => {
	const { emailVerified } = usePersonCommunicationData();
	const { getDataBoxById } = useDataBoxStoreLists();
	const dataBox = getDataBoxById(dataBoxId);
	const { extraConfig } = useExtraConfigContext();

	const formSchema = yup
		.object({
			shouldArchiveAutomatically: yup.boolean(),
			shouldArchiveLargeMessagesAutomatically: yup.boolean(),
			emailEnabled: yup.boolean(),
		})
		.required();

	const formMethods = useGovForm<DataboxAdditionalSettingsFormData>({
		formSchema,
		defaultValues: {
			shouldArchiveAutomatically: false,
			shouldArchiveLargeMessagesAutomatically: false,
			emailEnabled: false,
		},
	});

	const archivingQuery = useArchivingSettingsQuery(dataBoxId);
	const notificationSettingQuery = useNotificationSettingsQuery(dataBoxId);

	const formDefinitions = getFormDefinition({ formMethods, formSchema });

	useEffect(() => {
		if (archivingQuery.data) {
			const { klic, hodnota } = archivingQuery.data || {};
			if (klic && hodnota === DatovkaNastaveniArchivaceValue.DataBoxMessages) {
				formDefinitions.formMethods.setValue(
					'shouldArchiveAutomatically',
					true
				);
			}
			if (
				klic &&
				hodnota === DatovkaNastaveniArchivaceValue.LargeDataboxMessage
			) {
				formDefinitions.formMethods.setValue(
					'shouldArchiveAutomatically',
					true
				);
				formDefinitions.formMethods.setValue(
					'shouldArchiveLargeMessagesAutomatically',
					true
				);
			}
		}
	}, [archivingQuery.data, formDefinitions.formMethods]);

	useEffect(() => {
		if (notificationSettingQuery.data) {
			const { hodnota } = notificationSettingQuery.data;
			const { enabledSms, enabledEmail, enabledEmailAttachment } =
				notificationSettingsValues.decompose(hodnota);
			formDefinitions.formMethods.setValue(
				'emailEnabled',
				enabledEmail || enabledEmailAttachment || enabledSms
			);
		}
	}, [formDefinitions.formMethods, notificationSettingQuery.data]);

	const onSubmit = formMethods.handleSubmit(async () => {
		const values = formMethods.getValues();

		await archivingQuery.mutateAsync({
			value: values.shouldArchiveLargeMessagesAutomatically
				? DatovkaNastaveniArchivaceValue.LargeDataboxMessage
				: values.shouldArchiveAutomatically
					? DatovkaNastaveniArchivaceValue.DataBoxMessages
					: null,
		});

		await notificationSettingQuery.mutateAsync({
			value: notificationSettingsValues.additionalSettingsCompose(values),
		});

		return onSuccess();
	});

	const isLoading =
		archivingQuery.isMutating || notificationSettingQuery.isMutating;
	const isInitialLoading =
		archivingQuery.isLoading || notificationSettingQuery.isLoading;
	const isDisabled = isLoading || isInitialLoading;

	const isArchivingAvailable =
		dataBox?.typSchranky !== 'PO' &&
		dataBox?.typSchranky !== 'PO_REQ' &&
		extraConfig.pageRoutes.databoxConfig.displayAutoArchiveToggle;

	const showLargeMessageArchive = formDefinitions.formMethods.watch(
		'shouldArchiveAutomatically'
	);

	return {
		formId: `databox-additional-settings-${dataBoxId}`,
		formDefinitions,
		onSubmit,
		isLoading,
		isDisabled,
		isInitialLoading,
		emailVerified,
		isArchivingAvailable,
		showLargeMessageArchive,
	};
};
